/* eslint-disable no-undef */
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import { QueryClient, useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';

// Utils
import getServerSideTranslations from '../../src/utils/server-side';
import getServerDefaultLayout from '../../src/utils/getServerDefaultLayout';
import { pageSlugToTitle } from '../../src/utils/pageSlug';

// Hooks
import useSeoLinks from '../../src/hooks/useSeoLinks';
import useUrlHash from '../../src/hooks/useUrlHash';

// Services
import { sendContact, useHowCanHelpTypes, useResidences } from '../../src/services/contact';

// Components
import PageTransition from '../../components/templates/PageTransition/PageTransition';
import PageLayout from '../../components/templates/PageLayout/PageLayout';
import ContactLayout from '../../components/templates/ContactLayout/ContactLayout';
import Button from '../../components/atoms/Button/Button';
import HelpBoxInteractive from '../../components/atoms/HelpBoxInteractive/HelpBoxInteractive';
import ContactPhone from '../../components/organisms/ContactPhone/ContactPhone';
import BreadCrumb from '../../components/atoms/BreadCrumb/BreadCrumb';

// Forms
import ContactForm from '../../forms/ContactForm/ContactForm';
import contactFormValidation from '../../forms/ContactForm/ContactFormValidation';
import ContactPhoneForm from '../../forms/ContactPhoneForm/ContactPhoneForm';

// Constants
import {
  CONTACT_FORMS_LINKS, CONTACT_SLUG, FAQ_LINK, PHONE_NUMBER,
} from '../../src/constants';

function Contact({ header, footer }) {
  const isClient = typeof window !== 'undefined';
  const { locale, push } = useRouter();
  const { t } = useTranslation();
  const { canonicalUrl, alternateLinks } = useSeoLinks();
  const {
    residence, help, phone, number,
  } = useUrlHash();

  const [helpPhone, setHelpPhone] = useState(PHONE_NUMBER[locale]);
  const [hasHelpHash, setHasHelpHash] = useState(false);
  const [shouldExpandPhone, setShouldExpandPhone] = useState(false);

  const { data: optionsHelpTypes } = useHowCanHelpTypes();
  const { data: optionsResidences } = useResidences();

  const activeHelpTitle = useMemo(() => {
    if (optionsHelpTypes?.isLoading) return '';
    const activeHelp = optionsHelpTypes?.length ? optionsHelpTypes.find((option) => option.code === help) : '';
    return activeHelp ? activeHelp?.crmValue : '';
  }, [optionsHelpTypes, help]);

  const breadcrumbItems = [
    {
      name: t('breadcrumb.home'),
      position: 1,
      item: `/${locale}`,
    },
    {
      name: pageSlugToTitle(CONTACT_SLUG),
      position: 2,
      item: null,
    },
  ];

  const {
    handleSubmit,
    control: controlMail,
    register: registerMail,
    formState: { errors: errorsMail },
    reset,
    setValue,
  } = useForm({
    values: {
      contactMode: 'email',
      firstName: '',
      lastName: '',
      phone: null,
      email: '',
      residenceCrmId: '',
      howCanHelpTypeCode: '',
      comment: '',
      newsletter: false,
    },
    mode: 'onChange',
    resolver: yupResolver(contactFormValidation),
  });

  const { mutate, isLoading: isLoadingMutation } = useMutation({
    mutationFn: sendContact,
    mutationKey: 'sendContact',
    onSuccess: () => {
      reset();
      push('/contact/mail');
    },
  });

  useEffect(() => {
    // In order to render things differently on the client side we
    // need to use useEffect and trigger re-renders client side
    // because we need to use URL hash instead of search params and those
    // are not available on the server side

    if (isClient && number) {
      setHelpPhone(number?.replace(/(.{2})/g, '$1 ').trim());
    }
    if (isClient && phone) {
      setShouldExpandPhone(true);
    }
    if (isClient && residence) {
      setValue('residenceCrmId', residence);
    }
    if (isClient && help) {
      setValue('howCanHelpTypeCode', help);
      setHasHelpHash(true);
    }
  }, []);

  useEffect(() => {
    if (isClient && optionsResidences && residence) {
      const activeResidenceOption = optionsResidences?.length ? optionsResidences.find((option) => option.homingId === residence || option.mewsId === residence) : '';
      const activeResidenceValue = activeResidenceOption ? activeResidenceOption?.crmId : '';
      setValue('residenceCrmId', activeResidenceValue);
    }
  }, [optionsResidences]);

  const onSubmit = (data) => {
    const message = { ...data };
    mutate(message);
  };

  return (
    <>
      <Head>
        <title>{t('contact.metaTitle')}</title>
        <meta name="description" content={t('contact.metaDescription')} />

        <link rel="canonical" href={canonicalUrl} />
        {alternateLinks.map(({ href, hrefLang }) => (
          <link key={hrefLang} rel="alternate" href={href} hrefLang={hrefLang} />
        ))}
      </Head>

      <PageLayout
        header={header?.[0]?.data}
        footer={footer?.[0]?.data}
      >
        <PageTransition>
          <ContactLayout title={activeHelpTitle || t('contact.title')} imgSrc="/images/samples/contact.webp">
            <HelpBoxInteractive
              title={t('contact.form.emailMode.title')}
              isExpandedByDefault={!shouldExpandPhone && !hasHelpHash}
            >
              {() => (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <ContactForm control={controlMail} register={registerMail} errors={errorsMail}>
                    <Button
                      disabled={isLoadingMutation}
                      isLoading={isLoadingMutation}
                      isSubmit
                    >
                      {t('contact.form.submit')}
                    </Button>
                  </ContactForm>
                </form>
              )}
            </HelpBoxInteractive>

            <HelpBoxInteractive
              title={helpPhone}
              isExpandedByDefault={shouldExpandPhone}
            >
              {() => (
                <ContactPhone>
                  {(control, register, errors, setFormValue, isLoading) => (
                    <ContactPhoneForm
                      control={control}
                      register={register}
                      errors={errors}
                      setValue={setFormValue}
                    >
                      <Button
                        icon="send"
                        iconPosition="right"
                        disabled={isLoading}
                        isLoading={isLoading}
                        isSubmit
                      >
                        {t('contact.form.submitPhone')}
                      </Button>
                    </ContactPhoneForm>
                  )}
                </ContactPhone>
              )}
            </HelpBoxInteractive>

            <HelpBoxInteractive
              title={t('contact.helpTitleFaq')}
              isExpandedByDefault={hasHelpHash}
            >
              {() => (
                <div className="padding-left-40 padding-right-40 padding-bottom-40 padding-24-tablet flex-row flex-end w-100">
                  <Link href={FAQ_LINK[locale]} target="_blank">
                    <Button>
                      {t('contact.helpTextFaq')}
                    </Button>
                  </Link>
                </div>
              )}
            </HelpBoxInteractive>

            <p className="p2-desktop-default p2-mobile-default">
              <Trans
                i18nKey="contact.dataCollected"
                components={[
                  <Link
                    key="cgu"
                    href={CONTACT_FORMS_LINKS.collect[locale]}
                    target="_blank"
                    className="p2-desktop-bold p2-mobile-bold"
                  >
                    collect
                  </Link>,
                ]}
              />
            </p>

            <BreadCrumb items={breadcrumbItems} theme="transparent" noPadding />
          </ContactLayout>
        </PageTransition>
      </PageLayout>
    </>
  );
}

Contact.propTypes = {
  header: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,
  footer: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,
};

export async function getServerSideProps({ locale }) {
  const translations = await getServerSideTranslations(locale);

  const queryClient = new QueryClient();
  const { header, footer } = await getServerDefaultLayout(queryClient, locale, null, 'contact');

  return {
    props: {
      header,
      footer,
      ...translations,
    },
  };
}

export default Contact;
